import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';


interface Technology {
  id: string;
  name: string;
  category: string;
  icon: string;
  description: string;
  proficiency: number;
  yearsOfExperience: number;
  projects: string[];
  codeExample: {
    language: string;
    code: string;
  };
  keyFeatures: string[];
  useCases: string[];
  learningResources: { name: string; url: string }[];
}

const technologies: Technology[] = [
  {
    id: 'react',
    name: 'React',
    category: 'Frontend',
    icon: '⚛️',
    description: 'A JavaScript library for building user interfaces with component-based architecture.',
    proficiency: 95,
    yearsOfExperience: 4,
    projects: ['E-commerce Platform', 'Portfolio Website', 'Task Management App'],
    codeExample: {
      language: 'jsx',
      code: `function UserProfile({ user }) {
  const [isEditing, setIsEditing] = useState(false);
  
  return (
    <div className="profile-card">
      <img src={user.avatar} alt={user.name} />
      {isEditing ? (
        <EditForm user={user} onSave={() => setIsEditing(false)} />
      ) : (
        <UserInfo user={user} onEdit={() => setIsEditing(true)} />
      )}
    </div>
  );
}`
    },
    keyFeatures: ['Virtual DOM', 'Component Reusability', 'Hooks', 'JSX Syntax'],
    useCases: ['Single Page Applications', 'Progressive Web Apps', 'Mobile Apps with React Native'],
    learningResources: [
      { name: 'React Documentation', url: 'https://react.dev' },
      { name: 'React Patterns', url: 'https://reactpatterns.com' }
    ]
  },
  {
    id: 'python',
    name: 'Python',
    category: 'Backend',
    icon: '🐍',
    description: 'A versatile programming language perfect for web development, data science, and automation.',
    proficiency: 90,
    yearsOfExperience: 5,
    projects: ['AI Analytics Tool', 'Web Scraping System', 'API Development'],
    codeExample: {
      language: 'python',
      code: `from fastapi import FastAPI, HTTPException
from pydantic import BaseModel
from typing import List, Optional

app = FastAPI()

class User(BaseModel):
    id: int
    name: str
    email: str
    is_active: bool = True

@app.get("/users/{user_id}", response_model=User)
async def get_user(user_id: int):
    user = await database.fetch_user(user_id)
    if not user:
        raise HTTPException(status_code=404, detail="User not found")
    return user`
    },
    keyFeatures: ['Clean Syntax', 'Rich Libraries', 'Cross-platform', 'Dynamic Typing'],
    useCases: ['Web APIs', 'Data Analysis', 'Machine Learning', 'Automation Scripts'],
    learningResources: [
      { name: 'Python.org', url: 'https://python.org' },
      { name: 'Real Python', url: 'https://realpython.com' }
    ]
  },
  {
    id: 'typescript',
    name: 'TypeScript',
    category: 'Frontend',
    icon: '📘',
    description: 'JavaScript with static type definitions for better development experience and fewer bugs.',
    proficiency: 88,
    yearsOfExperience: 3,
    projects: ['Enterprise Dashboard', 'Type-safe API Client', 'Component Library'],
    codeExample: {
      language: 'typescript',
      code: `interface ApiResponse<T> {
  data: T;
  status: 'success' | 'error';
  message?: string;
}

class ApiClient {
  private baseUrl: string;
  
  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }
  
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    const response = await fetch(\`\${this.baseUrl}\${endpoint}\`);
    return response.json();
  }
}`
    },
    keyFeatures: ['Static Typing', 'IntelliSense', 'Compile-time Checks', 'Modern ES Features'],
    useCases: ['Large Applications', 'Team Development', 'API Integration', 'Library Development'],
    learningResources: [
      { name: 'TypeScript Handbook', url: 'https://typescriptlang.org' },
      { name: 'Type Challenges', url: 'https://github.com/type-challenges/type-challenges' }
    ]
  },
  {
    id: 'nodejs',
    name: 'Node.js',
    category: 'Backend',
    icon: '🟢',
    description: 'JavaScript runtime for building scalable server-side applications.',
    proficiency: 85,
    yearsOfExperience: 4,
    projects: ['REST API Server', 'Real-time Chat App', 'Microservices Architecture'],
    codeExample: {
      language: 'javascript',
      code: `const express = require('express');
const { createServer } = require('http');
const { Server } = require('socket.io');

const app = express();
const httpServer = createServer(app);
const io = new Server(httpServer);

app.use(express.json());

io.on('connection', (socket) => {
  console.log('User connected:', socket.id);
  
  socket.on('message', (data) => {
    io.emit('message', { ...data, timestamp: Date.now() });
  });
});

httpServer.listen(3000);`
    },
    keyFeatures: ['Event-driven', 'Non-blocking I/O', 'NPM Ecosystem', 'Cross-platform'],
    useCases: ['Web Servers', 'APIs', 'Real-time Applications', 'Build Tools'],
    learningResources: [
      { name: 'Node.js Docs', url: 'https://nodejs.org' },
      { name: 'Node.js Best Practices', url: 'https://github.com/goldbergyoni/nodebestpractices' }
    ]
  },
  {
    id: 'aws',
    name: 'AWS',
    category: 'Cloud',
    icon: '☁️',
    description: 'Amazon Web Services for cloud computing, storage, and deployment solutions.',
    proficiency: 80,
    yearsOfExperience: 3,
    projects: ['Serverless API', 'Static Website Hosting', 'Database Migration'],
    codeExample: {
      language: 'javascript',
      code: `const AWS = require('aws-sdk');
const dynamodb = new AWS.DynamoDB.DocumentClient();

exports.handler = async (event) => {
  try {
    const { id } = JSON.parse(event.body);
    
    const params = {
      TableName: 'Users',
      Key: { id },
      UpdateExpression: 'SET lastLogin = :timestamp',
      ExpressionAttributeValues: {
        ':timestamp': Date.now()
      }
    };
    
    await dynamodb.update(params).promise();
    
    return {
      statusCode: 200,
      body: JSON.stringify({ success: true })
    };
  } catch (error) {
    return {
      statusCode: 500,
      body: JSON.stringify({ error: error.message })
    };
  }
};`
    },
    keyFeatures: ['Scalability', 'Global Infrastructure', 'Pay-as-you-go', 'Managed Services'],
    useCases: ['Web Hosting', 'Data Storage', 'Serverless Computing', 'Content Delivery'],
    learningResources: [
      { name: 'AWS Documentation', url: 'https://docs.aws.amazon.com' },
      { name: 'AWS Training', url: 'https://aws.amazon.com/training' }
    ]
  },
  {
    id: 'docker',
    name: 'Docker',
    category: 'DevOps',
    icon: '🐳',
    description: 'Containerization platform for consistent development and deployment environments.',
    proficiency: 82,
    yearsOfExperience: 2,
    projects: ['Microservices Deployment', 'Development Environment', 'CI/CD Pipeline'],
    codeExample: {
      language: 'dockerfile',
      code: `FROM node:18-alpine

WORKDIR /app

COPY package*.json ./
RUN npm ci --only=production

COPY . .

EXPOSE 3000

USER node

CMD ["npm", "start"]

# docker-compose.yml
version: '3.8'
services:
  app:
    build: .
    ports:
      - "3000:3000"
    environment:
      - NODE_ENV=production
    depends_on:
      - db
  db:
    image: postgres:15
    environment:
      POSTGRES_DB: myapp`
    },
    keyFeatures: ['Containerization', 'Portability', 'Isolation', 'Scalability'],
    useCases: ['Application Deployment', 'Development Environments', 'Microservices', 'CI/CD'],
    learningResources: [
      { name: 'Docker Docs', url: 'https://docs.docker.com' },
      { name: 'Docker Hub', url: 'https://hub.docker.com' }
    ]
  }
];

const categories = [
  { name: 'All', icon: '🔧', color: 'from-gray-500 to-gray-600' },
  { name: 'Frontend', icon: '🎨', color: 'from-blue-500 to-purple-600' },
  { name: 'Backend', icon: '⚙️', color: 'from-green-500 to-teal-600' },
  { name: 'Cloud', icon: '☁️', color: 'from-cyan-500 to-blue-600' },
  { name: 'DevOps', icon: '🚀', color: 'from-orange-500 to-red-600' }
];



export default function TechStackExplorer() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTech, setSelectedTech] = useState<Technology | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'code' | 'projects'>('overview');

  const filteredTechnologies = selectedCategory === 'All' 
    ? technologies 
    : technologies.filter(tech => tech.category === selectedCategory);

  const getProficiencyColor = (proficiency: number) => {
    if (proficiency >= 90) return 'from-green-400 to-emerald-500';
    if (proficiency >= 80) return 'from-blue-400 to-cyan-500';
    if (proficiency >= 70) return 'from-yellow-400 to-orange-500';
    return 'from-red-400 to-pink-500';
  };

  return (
    <section id="tech-explorer" className="py-20 bg-gradient-to-br from-indigo-900 via-gray-900 to-purple-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Tech Stack <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">Explorer</span>
          </h2>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto">
            Dive deep into my technical expertise. Click on any technology to explore code examples, projects, and learning resources.
          </p>
          <div className="w-20 h-1 bg-gradient-to-r from-indigo-300 to-purple-300 mx-auto rounded-full mt-4"></div>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {categories.map((category) => (
            <motion.button
              key={category.name}
              onClick={() => setSelectedCategory(category.name)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                selectedCategory === category.name
                  ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                  : 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 border border-gray-600'
              }`}
            >
              <span className="text-lg">{category.icon}</span>
              <span>{category.name}</span>
            </motion.button>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Technology Cards */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-1 space-y-4"
          >
            <h3 className="text-xl font-semibold text-white mb-4">
              {selectedCategory === 'All' ? 'All Technologies' : selectedCategory} 
              <span className="text-gray-400 text-sm ml-2">({filteredTechnologies.length})</span>
            </h3>
            
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
              {filteredTechnologies.map((tech) => (
                <motion.button
                  key={tech.id}
                  onClick={() => setSelectedTech(tech)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full p-4 rounded-xl border transition-all duration-300 text-left ${
                    selectedTech?.id === tech.id
                      ? 'border-indigo-400 bg-indigo-400/10'
                      : 'border-gray-600 bg-gray-800/50 hover:border-indigo-400/50'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-2xl">{tech.icon}</span>
                    <div className="flex-1">
                      <h4 className="font-semibold text-white">{tech.name}</h4>
                      <p className="text-gray-400 text-sm">{tech.category}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-white">{tech.proficiency}%</div>
                      <div className="text-xs text-gray-400">{tech.yearsOfExperience}y exp</div>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <motion.div
                      className={`h-2 rounded-full bg-gradient-to-r ${getProficiencyColor(tech.proficiency)}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${tech.proficiency}%` }}
                      transition={{ duration: 1, delay: 0.5 }}
                    />
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Technology Details */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="lg:col-span-2"
          >
            {selectedTech ? (
              <div className="bg-gray-800/50 rounded-xl border border-gray-600 overflow-hidden">
                {/* Header */}
                <div className="p-6 border-b border-gray-600">
                  <div className="flex items-center space-x-4 mb-4">
                    <span className="text-4xl">{selectedTech.icon}</span>
                    <div>
                      <h3 className="text-2xl font-bold text-white">{selectedTech.name}</h3>
                      <p className="text-gray-400">{selectedTech.category} • {selectedTech.yearsOfExperience} years experience</p>
                    </div>
                  </div>
                  
                  <div className="flex space-x-1 mb-4">
                    {['overview', 'code', 'projects'].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab as any)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 capitalize ${
                          activeTab === tab
                            ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-400/30'
                            : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <AnimatePresence mode="wait">
                    {activeTab === 'overview' && (
                      <motion.div
                        key="overview"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="space-y-6"
                      >
                        <div>
                          <h4 className="text-lg font-semibold text-white mb-2">Description</h4>
                          <p className="text-gray-300">{selectedTech.description}</p>
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-white mb-3">Key Features</h4>
                          <div className="grid grid-cols-2 gap-2">
                            {selectedTech.keyFeatures.map((feature, index) => (
                              <div key={index} className="flex items-center space-x-2">
                                <div className="w-2 h-2 bg-indigo-400 rounded-full"></div>
                                <span className="text-gray-300 text-sm">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-white mb-3">Use Cases</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedTech.useCases.map((useCase, index) => (
                              <span
                                key={index}
                                className="px-3 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-sm"
                              >
                                {useCase}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-white mb-3">Learning Resources</h4>
                          <div className="space-y-2">
                            {selectedTech.learningResources.map((resource, index) => (
                              <a
                                key={index}
                                href={resource.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center space-x-2 text-indigo-400 hover:text-indigo-300 transition-colors"
                              >
                                <span>🔗</span>
                                <span>{resource.name}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                    
                    {activeTab === 'code' && (
                      <motion.div
                        key="code"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                      >
                        <h4 className="text-lg font-semibold text-white mb-4">Code Example</h4>
                        <div className="bg-gray-900/50 rounded-lg p-4 overflow-x-auto">
                          <pre className="text-sm text-gray-300 whitespace-pre-wrap">
                            <code>{selectedTech.codeExample.code}</code>
                          </pre>
                        </div>
                      </motion.div>
                    )}
                    
                    {activeTab === 'projects' && (
                      <motion.div
                        key="projects"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                      >
                        <h4 className="text-lg font-semibold text-white mb-4">Projects Using {selectedTech.name}</h4>
                        <div className="space-y-3">
                          {selectedTech.projects.map((project, index) => (
                            <div
                              key={index}
                              className="p-4 bg-gray-700/30 rounded-lg border border-gray-600"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="w-3 h-3 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-full"></div>
                                <span className="text-white font-medium">{project}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            ) : (
              <div className="bg-gray-800/30 rounded-xl border border-gray-600 p-12 text-center">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-semibold text-white mb-2">Select a Technology</h3>
                <p className="text-gray-400">
                  Click on any technology from the list to explore detailed information, code examples, and projects.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}